import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Layers, Droplets, Gauge, CheckCircle2, AlertCircle, ShieldAlert,
  ArrowDown, Save, Beaker, FlaskConical, Thermometer, Timer,
  TrendingUp, BarChart3, Filter,
} from "lucide-react";
import { useSampleLoader } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';

/* ─── Types ─────────────────────────────────────────────── */
type MediaType = 'greensand_plus' | 'birm' | 'pyrolusite';

interface GreensandParams {
  flowRate: number;        // m³/h
  influentFe: number;      // mg/L (as Fe³⁺ floc + residual Fe²⁺)
  influentMn: number;      // mg/L
  ph: number;
  doIn: number;            // mg/L dissolved O₂
  temperature: number;
  // Vessel
  vesselDiameter: number;  // m
  bedDepth: number;        // m
  freeboard: number;       // m
  numVessels: number;
  arrangement: 'duplex_duty_standby' | 'duplex_parallel' | 'triplex';
  // Media + chemistry
  media: MediaType;
  kmno4Dose: number;       // mg/L (Greensand Plus continuous regen, 0 for Birm)
  serviceVelocity: number; // m³/m²·h (informational target)
  backwashRate: number;    // m³/m²·h
}

interface GreensandSize {
  vesselArea: number;      // m² each
  totalArea: number;       // m² duty
  actualVelocity: number;  // m/h
  ebct: number;            // min (empty bed contact time)
  mediaVolume: number;     // m³ per vessel
  // Performance
  feRemovalPct: number;    // %
  mnRemovalPct: number;    // %
  outletFe: number;        // mg/L
  outletMn: number;        // mg/L
  kmno4ConsumptionKgD: number; // kg/day chemical consumption
  // Backwash
  backwashFlow: number;    // m³/h per vessel
  backwashVolume: number;  // m³ per cycle
  backwashDuration: number; // min
  // Pressure
  cleanHeadLoss: number;   // m
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overdesigned';
  statusDetail: string;
  breakthroughRisk: 'low' | 'moderate' | 'high';
  breakthroughReason: string;
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

const MEDIA_PROPS: Record<MediaType, {
  name: string; phMin: number; phMax: number;
  feCap: number; mnCap: number;
  needsChemical: boolean; needsDO: boolean;
  serviceMin: number; serviceMax: number; // m/h
  bedDensityKgPerM3: number;
  description: string;
}> = {
  greensand_plus: {
    name: 'Greensand Plus',
    phMin: 6.2, phMax: 8.8, feCap: 15, mnCap: 10,
    needsChemical: true, needsDO: false,
    serviceMin: 5, serviceMax: 12, bedDensityKgPerM3: 1360,
    description: 'Manganese-dioxide coated silica. Periodic or continuous KMnO₄ regen. Wide pH window.',
  },
  birm: {
    name: 'Birm',
    phMin: 6.8, phMax: 9.0, feCap: 10, mnCap: 1,
    needsChemical: false, needsDO: true,
    serviceMin: 6, serviceMax: 12, bedDensityKgPerM3: 700,
    description: 'Catalytic alumina silicate. No chemical required. Needs DO ≥15% of Fe. Limited Mn capacity.',
  },
  pyrolusite: {
    name: 'Pyrolusite (e.g. AquaMandix)',
    phMin: 6.5, phMax: 9.0, feCap: 20, mnCap: 15,
    needsChemical: false, needsDO: true,
    serviceMin: 8, serviceMax: 18, bedDensityKgPerM3: 1950,
    description: 'High-density natural MnO₂ ore. High Fe/Mn capacity but heavy — needs robust underdrain and high backwash flow.',
  },
};

/* ─── Calc engine ─── */
export function calcGreensand(p: GreensandParams): GreensandSize {
  const m = MEDIA_PROPS[p.media];
  const areaEach = Math.PI * Math.pow(p.vesselDiameter / 2, 2);
  // Duty area depends on arrangement
  const dutyVessels = p.arrangement === 'duplex_duty_standby' ? 1 : p.arrangement === 'duplex_parallel' ? 2 : 2;
  const totalArea = areaEach * dutyVessels;
  const actualVelocity = p.flowRate / totalArea;
  const mediaVolume = areaEach * p.bedDepth;
  const ebct = (mediaVolume * dutyVessels / p.flowRate) * 60; // min

  // ─── Removal efficiency model ───
  // Each media has Fe and Mn capacity (mg removed per g media before backwash needed);
  // here we compute removal efficiency vs operating envelope.
  const phPenalty = (p.ph < m.phMin || p.ph > m.phMax) ? 0.4 : 1.0;
  const velocityPenalty = actualVelocity > m.serviceMax ? Math.max(0.5, 1 - (actualVelocity - m.serviceMax) / m.serviceMax) : 1.0;
  const doPenalty = m.needsDO ? Math.min(1, p.doIn / Math.max(0.5, p.influentFe * 0.15 + p.influentMn * 0.3)) : 1.0;
  const chemPenalty = m.needsChemical && p.kmno4Dose <= 0 ? 0.5 : 1.0;

  const baseFeEff = p.media === 'greensand_plus' ? 0.97 : p.media === 'pyrolusite' ? 0.95 : 0.90;
  const baseMnEff = p.media === 'greensand_plus' ? 0.95 : p.media === 'pyrolusite' ? 0.92 : 0.40;

  const feRemovalPct = Math.min(99, baseFeEff * phPenalty * velocityPenalty * doPenalty * chemPenalty * 100);
  const mnRemovalPct = Math.min(99, baseMnEff * phPenalty * velocityPenalty * doPenalty * chemPenalty * 100);

  const outletFe = p.influentFe * (1 - feRemovalPct / 100);
  const outletMn = p.influentMn * (1 - mnRemovalPct / 100);

  // KMnO₄ consumption (only for Greensand Plus continuous regen)
  // ~1 mg KMnO₄ per mg Fe + ~2 mg per mg Mn (stoichiometric × 1.3 safety)
  const kmno4Required = (p.influentFe * 1.0 + p.influentMn * 2.0) * 1.3;
  const kmno4Effective = m.needsChemical ? Math.max(p.kmno4Dose, kmno4Required) : 0;
  const kmno4ConsumptionKgD = (kmno4Effective / 1000) * p.flowRate * 24; // kg/day

  // Backwash
  const backwashFlow = areaEach * p.backwashRate;
  const backwashVolume = mediaVolume * 4; // ~4 BV typical
  const backwashDuration = (backwashVolume / backwashFlow) * 60;

  // Clean head loss (rough Kozeny-Carman for catalytic media)
  const cleanHeadLoss = 0.4 + 0.05 * actualVelocity; // m approx

  // Status
  let status: GreensandSize['status']; let statusDetail: string;
  if (actualVelocity >= m.serviceMin && actualVelocity <= m.serviceMax && feRemovalPct >= 90) {
    status = 'optimal';
    statusDetail = `Optimal. Velocity ${actualVelocity.toFixed(1)} m/h within ${m.serviceMin}–${m.serviceMax} m/h. Fe ${feRemovalPct.toFixed(0)}%, Mn ${mnRemovalPct.toFixed(0)}%.`;
  } else if (actualVelocity <= m.serviceMax * 1.3 && feRemovalPct >= 75) {
    status = 'acceptable';
    statusDetail = `Acceptable. Monitor breakthrough at v=${actualVelocity.toFixed(1)} m/h.`;
  } else if (actualVelocity > m.serviceMax * 1.3) {
    status = 'undersized';
    statusDetail = `Undersized. v=${actualVelocity.toFixed(1)} m/h exceeds media envelope. Add vessel or increase diameter.`;
  } else {
    status = 'overdesigned';
    statusDetail = `Velocity ${actualVelocity.toFixed(1)} m/h below min. Capex could be reduced.`;
  }

  const breakthroughRisk: GreensandSize['breakthroughRisk'] = feRemovalPct < 80 ? 'high' : feRemovalPct < 90 ? 'moderate' : 'low';
  const breakthroughReason = phPenalty < 1 ? `pH ${p.ph} outside ${m.phMin}–${m.phMax} envelope` :
    chemPenalty < 1 ? `KMnO₄ dose ${p.kmno4Dose} mg/L below required ${kmno4Required.toFixed(2)} mg/L` :
    doPenalty < 1 ? `DO ${p.doIn} mg/L insufficient for Fe/Mn load` :
    velocityPenalty < 1 ? `Service velocity above media envelope` :
    'Within design envelope';

  return {
    vesselArea: areaEach, totalArea, actualVelocity, ebct, mediaVolume,
    feRemovalPct, mnRemovalPct, outletFe, outletMn, kmno4ConsumptionKgD,
    backwashFlow, backwashVolume, backwashDuration,
    cleanHeadLoss,
    status, statusDetail,
    breakthroughRisk, breakthroughReason,
  };
}

const DEFAULTS: GreensandParams = {
  flowRate: 24, influentFe: 0.45, influentMn: 0.05,
  ph: 7.8, doIn: 8, temperature: 12,
  vesselDiameter: 1.6, bedDepth: 0.9, freeboard: 0.5,
  numVessels: 2, arrangement: 'duplex_parallel',
  media: 'greensand_plus', kmno4Dose: 1.5,
  serviceVelocity: 8, backwashRate: 30,
};

function Slider({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {(() => { const v = Number.isFinite(value) ? value : 0; return step < 0.1 ? v.toFixed(2) : step < 1 ? v.toFixed(1) : v.toFixed(0); })()}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: color, color, ['--p' as any]: `${Math.max(0, Math.min(100, ((value - min) / Math.max(0.0001, (max - min))) * 100)).toFixed(2)}%` }} />
      {info && <p className="text-[10px] text-slate-600">{info}</p>}
    </div>
  );
}

function GaugeBar({ value, max, label, unit, color }: any) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1;
  const pct = Math.min(100, Math.max(0, (safeValue / safeMax) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold" style={{ color }}>{safeValue.toFixed(2)} {unit}</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

const LS_KEY = 'rb_greensand_spec';

export default function GreensandSpecPage() {
  const sampleLoader = useSampleLoader();
  const [params, setParams] = useState<GreensandParams>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return { ...DEFAULTS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const sp = sampleLoader.baseSampleParams as any;
    if (!sp) return;
    setParams(p => ({
      ...p,
      flowRate: sp.flowRate ? sp.flowRate / 24 : p.flowRate,
      influentFe: sp.fe ?? sp.iron ?? p.influentFe,
      influentMn: sp.mn ?? sp.manganese ?? p.influentMn,
      ph: sp.ph ?? p.ph,
      temperature: sp.temp ?? p.temperature,
    }));
  }, [sampleLoader.baseSampleParams]);

  const size = useMemo(() => calcGreensand(params), [params]);
  const m = MEDIA_PROPS[params.media];
  const update = <K extends keyof GreensandParams>(k: K, v: GreensandParams[K]) => setParams(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(params)); setSaved(true); toast.success('Greensand spec saved'); setTimeout(() => setSaved(false), 1500); }
    catch { toast.error('Save failed'); }
  };

  const statusColors: any = {
    optimal: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    acceptable: { bg: 'bg-sky-500/5', border: 'border-sky-500/20', text: 'text-sky-400', icon: CheckCircle2 },
    undersized: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', text: 'text-rose-400', icon: ShieldAlert },
    overdesigned: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  const scienceSections = [
    {
      title: 'Catalytic Fe/Mn removal mechanism',
      content: `Catalytic media (MnO₂ surface — natural in pyrolusite, coated in Greensand Plus, generated in-situ from KMnO₄ regen) accelerates the oxidation of Fe²⁺ and Mn²⁺. The oxidised metal floc is then mechanically captured in the bed. Greensand Plus operating envelope: pH ${m.phMin}–${m.phMax}, service velocity ${m.serviceMin}–${m.serviceMax} m/h. Current operating point: pH ${params.ph}, v ${size.actualVelocity.toFixed(1)} m/h.`,
    },
    {
      title: 'KMnO₄ stoichiometry',
      content: m.needsChemical
        ? `Continuous regeneration: KMnO₄ dose ≥ 1.0 × [Fe] + 2.0 × [Mn] mg/L (with 1.3× safety). For this water: ${(params.influentFe * 1.0 + params.influentMn * 2.0).toFixed(2)} mg/L stoichiometric → ${(params.influentFe * 1.0 + params.influentMn * 2.0 * 1.3).toFixed(2)} mg/L with safety. Current dose: ${params.kmno4Dose} mg/L. Daily consumption: ${size.kmno4ConsumptionKgD.toFixed(2)} kg/day. Excess KMnO₄ must NOT carry over to downstream RO membranes ("pink water" risk).`
        : `Selected media (${m.name}) does not require chemical regeneration — it relies on dissolved oxygen in the feed (≥15% of Fe by mass). Current DO ${params.doIn} mg/L vs requirement ${(params.influentFe * 0.15 + params.influentMn * 0.3).toFixed(2)} mg/L.`,
    },
    {
      title: 'Backwash hydraulics',
      content: `Backwash rate ${params.backwashRate} m³/m²·h × ${size.vesselArea.toFixed(2)} m² = ${size.backwashFlow.toFixed(1)} m³/h per vessel. Duration ~${size.backwashDuration.toFixed(0)} min (≈4 bed volumes). Air scour 50–60 Nm³/m²·h recommended for ${m.name}. Towns mains at 4 bar will typically not deliver this — break tank or recycle from filtered water tank.`,
    },
    {
      title: `Why ${m.name}`,
      content: m.description,
    },
  ];

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="Catalytic Fe/Mn Filter"
        subtitle="Greensand Plus / Birm / Pyrolusite — dissolved iron & manganese removal for RO pre-treatment"
        icon={Filter}
        right={<button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>}
      />

      <SampleSelector loader={sampleLoader} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="p-4 rb-card-sm">
            <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
              <ArrowDown className="w-3 h-3" /> Catalytic Filter — Aerated Water → Fe/Mn Removal → RO Feed
            </p>
            <svg viewBox="0 0 900 360" className="w-full h-auto">
              <defs>
                <linearGradient id="waterGradG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
                </linearGradient>
              </defs>
              <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">CATALYTIC FILTER — Fe/Mn REMOVAL</text>

              {/* Influent */}
              <rect x="30" y="55" width="100" height="100" rx="6" fill="url(#waterGradG)" stroke="#38bdf8" strokeWidth="2" />
              <text x="80" y="72" fill="#38bdf8" fontSize="9" textAnchor="middle" fontWeight="bold">Aerated In</text>
              <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 1)} m³/h</text>
              <text x="80" y="100" fill="#f43f5e" fontSize="8" textAnchor="middle">Fe {fmt(params.influentFe, 2)} mg/L</text>
              <text x="80" y="112" fill="#a855f7" fontSize="8" textAnchor="middle">Mn {fmt(params.influentMn, 2)} mg/L</text>
              <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">DO {fmt(params.doIn, 1)} mg/L</text>
              <text x="80" y="136" fill="#94a3b8" fontSize="8" textAnchor="middle">pH {fmt(params.ph, 1)}</text>

              {/* KMnO4 dosing if applicable */}
              {m.needsChemical && (
                <g transform="translate(160, 30)">
                  <rect x="0" y="0" width="80" height="40" rx="4" fill="#0f172a" stroke="#a855f7" strokeWidth="1.5" />
                  <text x="40" y="15" fill="#a855f7" fontSize="8" textAnchor="middle" fontWeight="bold">KMnO₄</text>
                  <text x="40" y="28" fill="#94a3b8" fontSize="7" textAnchor="middle">{fmt(params.kmno4Dose, 1)} mg/L</text>
                  <text x="40" y="55" fill="#a855f7" fontSize="7" textAnchor="middle">{fmt(size.kmno4ConsumptionKgD, 2)} kg/d</text>
                  <line x1="40" y1="40" x2="40" y2="80" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3 3" />
                  <polygon points="37,78 40,84 43,78" fill="#a855f7" />
                </g>
              )}

              <line x1="132" y1="105" x2="250" y2="105" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />
              <polygon points="248,102 255,105 248,108" fill="#38bdf8" />

              {/* Vessel 1 */}
              <rect x="260" y="55" width="130" height="200" rx="6" fill="url(#waterGradG)" stroke="#10b981" strokeWidth="2" />
              {/* Media bed */}
              <rect x="265" y="100" width="120" height="120" rx="2" fill="#10b981" opacity="0.18" />
              {Array.from({length: 40}).map((_, i) => (
                <circle key={i} cx={270 + (i * 11) % 110} cy={105 + Math.floor(i / 11) * 11 + (i % 2) * 4} r="1.4" fill="#10b981" opacity="0.55" />
              ))}
              <text x="325" y="75" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Vessel A</text>
              <text x="325" y="92" fill="#94a3b8" fontSize="7" textAnchor="middle">Ø{fmt(params.vesselDiameter, 2)}m</text>
              <text x="325" y="170" fill="#10b981" fontSize="8" textAnchor="middle">{m.name}</text>
              <text x="325" y="184" fill="#94a3b8" fontSize="7" textAnchor="middle">bed {fmt(params.bedDepth, 2)}m</text>
              <text x="325" y="235" fill="#94a3b8" fontSize="8" textAnchor="middle">v {fmt(size.actualVelocity, 1)} m/h</text>
              <text x="325" y="247" fill="#94a3b8" fontSize="8" textAnchor="middle">EBCT {fmt(size.ebct, 1)} min</text>

              {/* Vessel 2 (if duplex parallel) */}
              {params.arrangement !== 'duplex_duty_standby' && (
                <>
                  <rect x="400" y="55" width="130" height="200" rx="6" fill="url(#waterGradG)" stroke="#10b981" strokeWidth="2" />
                  <rect x="405" y="100" width="120" height="120" rx="2" fill="#10b981" opacity="0.18" />
                  {Array.from({length: 40}).map((_, i) => (
                    <circle key={`v2${i}`} cx={410 + (i * 11) % 110} cy={105 + Math.floor(i / 11) * 11 + (i % 2) * 4} r="1.4" fill="#10b981" opacity="0.55" />
                  ))}
                  <text x="465" y="75" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Vessel B</text>
                  <text x="465" y="170" fill="#10b981" fontSize="8" textAnchor="middle">parallel duty</text>
                </>
              )}

              <line x1={params.arrangement === 'duplex_duty_standby' ? 392 : 532} y1="155" x2="575" y2="155" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
              <polygon points="573,152 580,155 573,158" fill="#10b981" />

              {/* RO-ready effluent */}
              <rect x="585" y="115" width="115" height="80" rx="6" fill="url(#waterGradG)" stroke="#10b981" strokeWidth="2" />
              <text x="642" y="132" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">To RO Polish</text>
              <text x="642" y="146" fill="#10b981" fontSize="8" textAnchor="middle">Fe {fmt(size.outletFe, 3)} mg/L</text>
              <text x="642" y="158" fill="#10b981" fontSize="8" textAnchor="middle">Mn {fmt(size.outletMn, 3)} mg/L</text>
              <text x="642" y="170" fill="#94a3b8" fontSize="8" textAnchor="middle">RO target ≤0.05/0</text>
              <text x="642" y="184" fill={size.outletFe <= 0.05 ? '#10b981' : '#f43f5e'} fontSize="8" textAnchor="middle">
                {size.outletFe <= 0.05 ? '✓ Within target' : '⚠ Above target'}
              </text>

              {/* Backwash recycle indication */}
              <rect x="260" y="275" width="270" height="40" rx="4" fill="#0f172a" stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 3" />
              <text x="395" y="290" fill="#fbbf24" fontSize="8" textAnchor="middle">Backwash {fmt(size.backwashFlow, 1)} m³/h · {fmt(size.backwashDuration, 0)} min · {fmt(size.backwashVolume, 1)} m³/cycle</text>
              <text x="395" y="305" fill="#94a3b8" fontSize="7" textAnchor="middle">Air scour 50–60 Nm³/m²·h · Effluent to drain</text>

              {/* KPI box */}
              <g transform="translate(720, 50)">
                <rect x="0" y="0" width="160" height="280" rx="6" fill="#0f172a" stroke="#1e293b" strokeWidth="1" />
                <text x="80" y="16" fill="#94a3b8" fontSize="9" textAnchor="middle" fontWeight="bold">PERFORMANCE</text>
                <text x="10" y="38" fill="#64748b" fontSize="8">Fe removal</text>
                <text x="150" y="38" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.feRemovalPct, 0)}%</text>
                <text x="10" y="58" fill="#64748b" fontSize="8">Mn removal</text>
                <text x="150" y="58" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.mnRemovalPct, 0)}%</text>
                <text x="10" y="78" fill="#64748b" fontSize="8">Outlet Fe</text>
                <text x="150" y="78" fill={size.outletFe <= 0.05 ? '#10b981' : '#f43f5e'} fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.outletFe, 3)}</text>
                <text x="10" y="98" fill="#64748b" fontSize="8">Outlet Mn</text>
                <text x="150" y="98" fill={size.outletMn <= 0.05 ? '#10b981' : '#f43f5e'} fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.outletMn, 3)}</text>
                <text x="10" y="118" fill="#64748b" fontSize="8">v service</text>
                <text x="150" y="118" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.actualVelocity, 1)} m/h</text>
                <text x="10" y="138" fill="#64748b" fontSize="8">EBCT</text>
                <text x="150" y="138" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.ebct, 1)} min</text>
                <text x="10" y="158" fill="#64748b" fontSize="8">Bed volume</text>
                <text x="150" y="158" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.mediaVolume, 2)} m³</text>
                <text x="10" y="178" fill="#64748b" fontSize="8">Head loss (clean)</text>
                <text x="150" y="178" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.cleanHeadLoss, 2)} m</text>
                {m.needsChemical && (
                  <>
                    <text x="10" y="198" fill="#64748b" fontSize="8">KMnO₄ daily</text>
                    <text x="150" y="198" fill="#a855f7" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.kmno4ConsumptionKgD, 2)} kg</text>
                  </>
                )}
                <text x="10" y="220" fill="#64748b" fontSize="8">Backwash flow</text>
                <text x="150" y="220" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.backwashFlow, 0)} m³/h</text>
                <text x="10" y="240" fill="#64748b" fontSize="8">Backwash vol</text>
                <text x="150" y="240" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.backwashVolume, 1)} m³</text>
                <text x="10" y="260" fill="#64748b" fontSize="8">Breakthrough</text>
                <text x="150" y="260" fill={size.breakthroughRisk === 'low' ? '#10b981' : size.breakthroughRisk === 'moderate' ? '#fbbf24' : '#f43f5e'} fontSize="9" textAnchor="end" fontWeight="bold">{size.breakthroughRisk}</text>
              </g>
            </svg>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-5">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>

          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          {size.breakthroughRisk !== 'low' && (
            <div className="rounded-xl border p-3 bg-rose-500/5 border-rose-500/20">
              <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-400" /><span className="text-xs font-bold uppercase text-rose-400">Breakthrough risk: {size.breakthroughRisk}</span></div>
              <p className="text-[10px] mt-1 leading-relaxed text-rose-400 opacity-80">{size.breakthroughReason}</p>
            </div>
          )}

          {/* Media selector */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Media Selection</h3>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(MEDIA_PROPS) as MediaType[]).map(k => {
                const mp = MEDIA_PROPS[k];
                const active = params.media === k;
                return (
                  <button key={k} onClick={() => update('media', k)} className={`text-left p-2 rounded-lg border transition-all ${active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
                    <p className={`text-xs font-bold ${active ? 'text-emerald-400' : 'text-slate-300'}`}>{mp.name}</p>
                    <p className="text-[9px] text-slate-500 leading-tight mt-0.5">pH {mp.phMin}–{mp.phMax} · {mp.needsChemical ? 'KMnO₄' : 'no chem'}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 leading-relaxed">{m.description}</p>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Water In</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Flow Rate" value={params.flowRate} min={1} max={200} step={1} unit="m³/h" icon={Droplets} color="#38bdf8" onChange={(v: number) => update('flowRate', v)} />
              <Slider label="Influent Fe" value={params.influentFe} min={0} max={5} step={0.01} unit="mg/L" icon={FlaskConical} color="#f43f5e" onChange={(v: number) => update('influentFe', v)} />
              <Slider label="Influent Mn" value={params.influentMn} min={0} max={2} step={0.01} unit="mg/L" icon={FlaskConical} color="#a855f7" onChange={(v: number) => update('influentMn', v)} />
              <Slider label="pH" value={params.ph} min={5} max={9.5} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => update('ph', v)} />
              <Slider label="DO in" value={params.doIn} min={0} max={12} step={0.1} unit="mg/L" icon={Droplets} color="#06b6d4" onChange={(v: number) => update('doIn', v)} info="From upstream aeration" />
              <Slider label="Temperature" value={params.temperature} min={2} max={30} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={(v: number) => update('temperature', v)} />
            </div>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Vessel Design</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Vessel Diameter" value={params.vesselDiameter} min={0.5} max={3} step={0.05} unit="m" icon={Gauge} color="#10b981" onChange={(v: number) => update('vesselDiameter', v)} />
              <Slider label="Bed Depth" value={params.bedDepth} min={0.5} max={1.5} step={0.05} unit="m" icon={Layers} color="#10b981" onChange={(v: number) => update('bedDepth', v)} />
              <Slider label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.05} unit="m" icon={ArrowDown} color="#94a3b8" onChange={(v: number) => update('freeboard', v)} />
              <Slider label="Number of Vessels" value={params.numVessels} min={1} max={4} step={1} unit="" icon={Layers} color="#64748b" onChange={(v: number) => update('numVessels', v)} />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 mb-1">Arrangement</p>
              <select value={params.arrangement} onChange={e => update('arrangement', e.target.value as any)} className="w-full bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
                <option value="duplex_parallel">Duplex parallel (both online, no interrupt on backwash)</option>
                <option value="duplex_duty_standby">Duplex duty/standby (one online, one backwashing)</option>
                <option value="triplex">Triplex (2 duty + 1 standby)</option>
              </select>
            </div>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Chemistry &amp; Backwash</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {m.needsChemical && (
                <Slider label="KMnO₄ Dose" value={params.kmno4Dose} min={0} max={10} step={0.1} unit="mg/L" icon={FlaskConical} color="#a855f7" onChange={(v: number) => update('kmno4Dose', v)} info="Continuous regen, stoichiometric × 1.3" />
              )}
              <Slider label="Backwash Rate" value={params.backwashRate} min={15} max={60} step={1} unit="m³/m²·h" icon={TrendingUp} color="#fbbf24" onChange={(v: number) => update('backwashRate', v)} />
            </div>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-200">Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <GaugeBar value={size.feRemovalPct} max={100} label="Fe removal" unit="%" color="#10b981" />
              <GaugeBar value={size.mnRemovalPct} max={100} label="Mn removal" unit="%" color="#10b981" />
              <GaugeBar value={size.outletFe} max={0.5} label="Outlet Fe" unit="mg/L" color={size.outletFe <= 0.05 ? '#10b981' : '#f43f5e'} />
              <GaugeBar value={size.outletMn} max={0.2} label="Outlet Mn" unit="mg/L" color={size.outletMn <= 0.05 ? '#10b981' : '#f43f5e'} />
            </div>
          </div>

          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <div className="space-y-3">
              {scienceSections.map((s, i) => (
                <details key={i} className="rounded-lg border border-slate-800 bg-rb-elevated/40">
                  <summary className="cursor-pointer p-3 text-xs font-bold text-sky-400 hover:text-sky-300">{s.title}</summary>
                  <p className="px-3 pb-3 text-[11px] text-slate-300 leading-relaxed">{s.content}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
